// Building a topic's lesson, shared by the learner's Skill Tree and the
// teacher's student dashboard.
//
// This used to live inside LanguageTreeView. The teacher's "Create & assign"
// needs precisely the same lesson a learner would get — if the two drifted
// apart, a teacher would be setting homework that differs from the tree.
import { streamOllamaGenerate } from '../ollama'
import { getBuiltinLesson } from '../data/lessons'

const S = (v) => String(v || '').trim()

export const parseObj = (raw) => {
  let s = String(raw || '').replace(/```json/gi, '').replace(/```/g, '').trim()
  const a = s.indexOf('{'), b = s.lastIndexOf('}')
  if (a >= 0 && b > a) s = s.slice(a, b + 1)
  try { return JSON.parse(s) } catch { return null }
}
const pairs = (arr) => (Array.isArray(arr) ? arr : []).map(x => ({ tr: S(x?.tr), en: S(x?.en) })).filter(p => p.tr)

// Normalise a generated lesson into the shape StudentLessonView renders.
export const cleanLesson = (o, code, name, level) => {
  if (!o) return null
  const sections = (Array.isArray(o.sections) ? o.sections : []).map(sec => {
    const ex = sec?.exercise
    const items = Array.isArray(ex?.items) ? ex.items.map(it => ({ q: S(it?.q), a: S(it?.a), options: Array.isArray(it?.options) ? it.options.map(S).filter(Boolean) : undefined })).filter(it => it.q || it.a) : []
    // Paradigm table: columns + rows of strings (rendered by StudentLessonView).
    const tb = sec?.table
    const table = tb && Array.isArray(tb.rows) && tb.rows.length
      ? { columns: (Array.isArray(tb.columns) ? tb.columns : []).map(S), rows: tb.rows.map(r => (Array.isArray(r) ? r : []).map(S)).filter(r => r.length) }
      : undefined
    return {
      kind: ['concept', 'vocab', 'paradigm', 'patterns', 'practice'].includes(sec?.kind) ? sec.kind : (items.length ? 'practice' : 'concept'),
      title: S(sec?.title) || 'Section', note: S(sec?.note) || undefined, table,
      words: pairs(sec?.words), sentences: pairs(sec?.sentences),
      exercise: items.length ? { type: S(ex?.type) || 'translate', prompt: S(ex?.prompt), items } : undefined,
    }
  }).filter(s => s.words.length || s.sentences.length || s.exercise || s.note || s.table)
  const r = o.reading
  const reading = S(r?.text) ? { title: S(r?.title) || 'Reading', text: S(r.text), glossary: pairs(r?.glossary), questions: (Array.isArray(r?.questions) ? r.questions : []).map(q => ({ q: S(q?.q), a: S(q?.a) })).filter(q => q.q) } : undefined
  const d = o.dialogue
  const dLines = (Array.isArray(d?.lines) ? d.lines : []).map(l => ({ who: S(l?.who), tr: S(l?.tr), en: S(l?.en) })).filter(l => l.tr)
  const dialogue = dLines.length ? { title: S(d?.title) || 'Mini dialogue', lines: dLines } : undefined
  const speaking = (Array.isArray(o.speaking) ? o.speaking : []).map(S).filter(Boolean)
  if (sections.length === 0 && !reading && !dialogue) return null
  return { title: S(o.title) || 'Lesson', level: S(o.level) || level, goal: S(o.goal), language: code, languageName: name, sections, reading, dialogue, speaking: speaking.length ? speaking : undefined }
}

export const lessonPrompt = (t, langName, personal, exemplar) => {
  const level = t.level || 'A1'
  const audience = personal
    ? `for a specific learner — weave THEIR real details into the examples naturally where it fits (use their name, city, interests). Do NOT invent facts about them. ${personal}`
    : `for an English speaker learning ${langName}. Use neutral, generic examples — do NOT reference any specific real person, name, city, job or hobby; use common textbook names/places if a name is needed.`
  const style = exemplar
    ? `\nHere is the CANONICAL lesson for this topic. Keep its structure, grammar explanations, tables, section order and difficulty; only adapt example sentences, vocabulary contexts, the reading and the dialogue to the learner. Every grammar fact must stay exactly correct.\n${JSON.stringify(exemplar)}\n`
    : ''
  return `Create a ${langName} workbook lesson for the topic "${t.title}" (CEFR ${level}) ${audience}
${style}
Write it like a good teacher's workbook, in this order (5-7 sections):
1) "concept": a 1-2 sentence "note" explaining the grammar point in plain English, then 4-6 example "sentences" IN ${langName} with English.
2) "paradigm": a "table" showing the forms — "columns" (e.g. Pronoun | ${langName} | English) and 4-6 "rows"; put vowel-harmony / spelling rules in "note".
3) "vocab": 8-12 useful ${langName} "words" for the topic with English (recycle common beginner words).
4) "patterns": 4-8 model "sentences" IN ${langName} with English that combine the grammar with the vocab.
5) two or three "practice" sections, each with ONE "exercise" where the learner must answer IN ${langName}:
 - "fill": q = a ${langName} sentence with exactly one blank ____ (English hint in "prompt"); a = the missing ${langName} word.
 - "choose": q = a short question; "options" = 3 plausible ${langName} choices; a = the correct option (one of options).
 - "translate": q = a short English sentence; a = its ${langName} translation.
 - "match": items pair q (${langName}) with a (English).
Then "reading": a 6-10 sentence ${langName} passage that REUSES the lesson vocabulary, a "glossary" of 3-5 new words, and 3-4 "questions" whose answers "a" are SHORT ${langName} phrases from the text.
Then "dialogue": ONE coherent mini-conversation between two people (A and B) in a concrete everyday situation (e.g. at a café, meeting a friend, at school). 6-8 short lines. Every line must respond to the line before it — a question is answered in the next line, an answer is followed up — so it reads like a real exchange with a beginning and an end, NOT a list of unrelated example sentences. Reuse the lesson vocabulary and the grammar point.
Then "speaking": 3 prompts in English asking the learner to say something about themselves using the grammar.

STRICT: every "tr", every exercise "a", every "options" entry, every ${langName}-column table cell, and every reading answer "a" MUST be in ${langName}. English appears ONLY in "en", "note", "prompt", translate/choose question "q", and speaking prompts. True to CEFR ${level}. All ${langName} must be grammatically correct.
Return ONLY minified JSON: {"title":"","level":"${level}","goal":"","sections":[{"kind":"concept|paradigm|vocab|patterns|practice","title":"","note":"","table":{"columns":[],"rows":[[]]},"words":[{"tr":"","en":""}],"sentences":[{"tr":"","en":""}],"exercise":{"type":"fill|choose|translate|match","prompt":"","items":[{"q":"","a":"","options":[]}]}}],"reading":{"title":"","text":"","glossary":[{"tr":"","en":""}],"questions":[{"q":"","a":""}]},"dialogue":{"title":"","lines":[{"who":"","tr":"","en":""}]},"speaking":[""]}`
}

// Generate + save a lesson as a Meeting. Returns { lessonObj, meetingId }.
//
// A hand-authored lesson (src/data/lessons) is saved as-is — instant, correct,
// no model call. Otherwise the model writes one, and any authored lesson for
// the topic is handed over as the exemplar so a generated copy keeps its shape.
export async function buildTopicLesson({
  topic, code, langName, username, personal = null, addMeeting, onProgress,
}) {
  const level = topic.level || 'A1'
  const prebuilt = personal ? null : getBuiltinLesson(code, topic.slug)
  let lessonObj
  if (prebuilt) {
    lessonObj = cleanLesson(prebuilt, code, langName, level)
  } else {
    const raw = await streamOllamaGenerate({
      serverUrl: process.env.REACT_APP_OLLAMA_URI,
      apiKey: process.env.REACT_APP_OLLAMA_KEY,
      body: {
        model: 'gemma3:27b',
        prompt: lessonPrompt(topic, langName, personal, getBuiltinLesson(code, topic.slug)),
        format: 'json',
        options: { temperature: 0.4, num_predict: 4500 },
      },
      onProgress: (acc) => { if (onProgress) onProgress(acc) },
    })
    lessonObj = cleanLesson(parseObj(raw), code, langName, level)
  }
  if (!lessonObj) throw new Error('Could not build a lesson — try again.')

  const words = []
  const sentences = []
  lessonObj.sections.forEach((s) => {
    ;(s.words || []).forEach((w) => words.push({ word: w.tr, translation: w.en, lang: code }))
    ;(s.sentences || []).forEach((se) => sentences.push({ sourceText: se.tr, targetText: se.en, sourceLang: code, targetLang: 'eng' }))
  })
  const saved = await addMeeting({
    variables: { input: {
      meeting: {
        title: `${topic.title}${personal ? ' (personal)' : ''} — ${langName}`,
        type: 'Language Class',
        description: `${level} · ${lessonObj.goal || topic.blurb || ''}`.slice(0, 240),
        meetingDate: new Date().toISOString().split('T')[0],
        username,
        lessonData: JSON.stringify(lessonObj),
      },
      sentences, words, tasks: [],
    } },
  })
  return { lessonObj, meetingId: saved.data?.addMeetingWithContent?.meeting?.identity }
}
